import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import '../styles.css';

export default function MerciPage() {
  const { api, currentGroupeId, user } = useAuth();
  const [merci, setMerci] = useState([]);
  const [membres, setMembres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [tab, setTab] = useState('tous'); // tous | donnes | recus

  const loadData = useCallback(() => {
    if (!currentGroupeId) { setLoading(false); return; }
    Promise.all([
      api.get('/mpb', { params: { groupe_id: currentGroupeId } }),
      api.get('/contacts', { params: { groupe_id: currentGroupeId, statut: 'membre' } }),
    ]).then(([mpbRes, membresRes]) => {
      setMerci(mpbRes.data);
      setMembres(membresRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [api, currentGroupeId]);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = merci.filter(m => {
    if (tab === 'donnes') return m.donneur_id === user?.contact_id;
    if (tab === 'recus') return m.receveur_id === user?.contact_id;
    return true;
  });

  const totalCA = merci.filter(m => m.statut === 'deal').reduce((sum, m) => sum + (parseFloat(m.montant) || 0), 0);
  const nbLeads = merci.filter(m => m.statut === 'lead').length;
  const nbDeals = merci.filter(m => m.statut === 'deal').length;

  if (showForm) return (
    <MerciForm
      membres={membres}
      groupeId={currentGroupeId}
      api={api}
      onSave={() => { loadData(); setShowForm(false); }}
      onCancel={() => setShowForm(false)}
    />
  );

  return (
    <div style={{ minHeight: '100vh' }}>
      <div style={{ padding: '12px 20px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1c1c1e' }}>Merci Business</h1>
        <button onClick={() => setShowForm(true)} className="mb-btn mb-btn-primary mb-btn-sm">+ Merci</button>
      </div>

      <div className="mb-content" style={{ paddingTop: 0 }}>
        {/* KPIs */}
        <div className="mb-stat-grid mb-mb-md">
          <div className="mb-stat-card">
            <div className="mb-stat-accent" style={{ background: '#95C11F' }} />
            <div className="mb-stat-label">CA total (deals)</div>
            <div className="mb-stat-value" style={{ fontSize: 20, color: '#95C11F' }}>{formatCA(totalCA)}</div>
          </div>
          <div className="mb-stat-card">
            <div className="mb-stat-accent" style={{ background: '#3C2618' }} />
            <div className="mb-stat-label">Leads en cours</div>
            <div className="mb-stat-value" style={{ color: '#3C2618' }}>{nbLeads}</div>
          </div>
          <div className="mb-stat-card">
            <div className="mb-stat-accent" style={{ background: '#95C11F' }} />
            <div className="mb-stat-label">Deals signés</div>
            <div className="mb-stat-value" style={{ color: '#95C11F' }}>{nbDeals}</div>
          </div>
          <div className="mb-stat-card">
            <div className="mb-stat-accent" style={{ background: '#3C2618' }} />
            <div className="mb-stat-label">Taux closing</div>
            <div className="mb-stat-value" style={{ fontSize: 20 }}>{merci.length > 0 ? Math.round(nbDeals * 100 / merci.length) : 0}%</div>
          </div>
        </div>

        {/* Filtres */}
        <div style={{ display: 'flex', background: '#e5e5ea', borderRadius: 10, padding: 2, marginBottom: 14 }}>
          {[['tous','Tous'],['donnes','Donnés'],['recus','Reçus']].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              style={{ flex: 1, padding: '6px', borderRadius: 8, border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer', background: tab === key ? 'white' : 'transparent', color: tab === key ? '#1c1c1e' : '#8e8e93', boxShadow: tab === key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
              {label}
            </button>
          ))}
        </div>

        {loading ? <div className="mb-loading"><div className="mb-spinner" /></div> :
         filtered.length === 0 ? (
           <div className="mb-empty mb-card">
             <div className="mb-empty-title">Aucun Merci enregistré</div>
             <div className="mb-empty-sub">Cliquez sur "+ Merci" pour enregistrer votre premier Merci pour le Business.</div>
           </div>
         ) : (
           filtered.map(m => (
             <MerciCard key={m.id} merci={m} api={api} onRefresh={loadData} />
           ))
         )}
      </div>
    </div>
  );
}

function MerciCard({ merci, api, onRefresh }) {
  const [converting, setConverting] = useState(false);
  const [showConvert, setShowConvert] = useState(false);
  const [montant, setMontant] = useState('');

  const handleConvert = async () => {
    if (!montant || isNaN(parseFloat(montant))) return toast.error('Montant invalide');
    setConverting(true);
    try {
      await api.put(`/mpb/${merci.id}`, { statut: 'deal', montant: parseFloat(montant) });
      toast.success('Deal confirmé ! 🎉');
      onRefresh();
    } catch { toast.error('Erreur'); }
    finally { setConverting(false); setShowConvert(false); }
  };

  const isLead = merci.statut === 'lead';
  const date = new Date(merci.date_mpb).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="mb-card mb-mb-md">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#95C11F', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: 'white', flexShrink: 0 }}>
            {merci.donneur_prenom?.[0]}{merci.donneur_nom?.[0]}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1c1c1e' }}>{merci.donneur_prenom} {merci.donneur_nom}</div>
            <div style={{ fontSize: 11, color: '#8e8e93' }}>→ {merci.receveur_prenom} {merci.receveur_nom}</div>
          </div>
        </div>
        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20, background: isLead ? '#fff3e0' : '#f0f8e1', color: isLead ? '#b45309' : '#3B6D11' }}>
          {isLead ? 'Lead' : 'Deal signé'}
        </span>
      </div>

      {merci.description && <p style={{ fontSize: 13, color: '#3c3c43', marginBottom: 8, lineHeight: 1.5 }}>{merci.description}</p>}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 11, color: '#8e8e93' }}>{date}</span>
        {merci.montant && <span style={{ fontSize: 15, fontWeight: 700, color: '#95C11F' }}>{formatCA(merci.montant)}</span>}
      </div>

      {isLead && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '0.5px solid #f2f2f7' }}>
          {showConvert ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="number" value={montant} onChange={e => setMontant(e.target.value)}
                className="mb-input" style={{ flex: 1, padding: '8px 12px' }} placeholder="Montant du deal (€)" autoFocus />
              <button onClick={handleConvert} disabled={converting} className="mb-btn mb-btn-primary mb-btn-sm">✓</button>
              <button onClick={() => setShowConvert(false)} className="mb-btn mb-btn-sm" style={{ background: '#f2f2f7', color: '#3c3c43' }}>✕</button>
            </div>
          ) : (
            <button onClick={() => setShowConvert(true)} className="mb-btn mb-btn-ghost" style={{ width: '100%', justifyContent: 'center', fontSize: 13 }}>
              🎉 Confirmer comme Deal signé
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function MerciForm({ membres, groupeId, api, onSave, onCancel }) {
  const [form, setForm] = useState({ donneur_id: '', receveur_id: '', statut: 'lead', montant: '', description: '', date_mpb: new Date().toISOString().split('T')[0] });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.donneur_id || !form.receveur_id) return toast.error('Sélectionnez le donneur et le receveur');
    if (form.donneur_id === form.receveur_id) return toast.error('Le donneur et le receveur doivent être différents');
    setSaving(true);
    try {
      await api.post('/mpb', { ...form, groupe_id: groupeId, montant: form.montant ? parseFloat(form.montant) : null });
      toast.success('Merci enregistré !');
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ minHeight: '100vh' }}>
      <div style={{ padding: '12px 20px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button onClick={onCancel} style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#95C11F', fontSize: 15, fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer' }}>
          <svg width="10" height="16" viewBox="0 0 10 16" fill="none" stroke="#95C11F" strokeWidth="2.5"><path d="M8 2L2 8l6 6"/></svg> Retour
        </button>
        <h1 style={{ fontSize: 17, fontWeight: 700, color: '#1c1c1e' }}>Nouveau Merci</h1>
        <button onClick={handleSubmit} disabled={saving} className="mb-btn mb-btn-primary mb-btn-sm">{saving ? '...' : 'Enregistrer'}</button>
      </div>

      <div className="mb-content" style={{ paddingTop: 0 }}>
        <div className="mb-card mb-mb-md">
          <div className="mb-card-title">Type de Merci</div>
          <div style={{ display: 'flex', background: '#e5e5ea', borderRadius: 10, padding: 2, marginBottom: 14 }}>
            {[['lead','Lead transmis'],['deal','Deal signé']].map(([val, label]) => (
              <button key={val} onClick={() => set('statut', val)}
                style={{ flex: 1, padding: '8px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: form.statut === val ? (val === 'deal' ? '#95C11F' : '#3C2618') : 'transparent', color: form.statut === val ? 'white' : '#8e8e93' }}>
                {label}
              </button>
            ))}
          </div>

          <div className="mb-field">
            <label className="mb-label">Qui transmet le Merci ? *</label>
            <select value={form.donneur_id} onChange={e => set('donneur_id', e.target.value)} className="mb-select">
              <option value="">-- Sélectionner un membre --</option>
              {membres.map(m => <option key={m.id} value={m.id}>{m.prenom} {m.nom} — {m.societe}</option>)}
            </select>
          </div>

          <div className="mb-field">
            <label className="mb-label">Qui reçoit le Merci ? *</label>
            <select value={form.receveur_id} onChange={e => set('receveur_id', e.target.value)} className="mb-select">
              <option value="">-- Sélectionner un membre --</option>
              {membres.filter(m => m.id !== form.donneur_id).map(m => <option key={m.id} value={m.id}>{m.prenom} {m.nom} — {m.societe}</option>)}
            </select>
          </div>

          {form.statut === 'deal' && (
            <div className="mb-field">
              <label className="mb-label">Montant du deal (€)</label>
              <input type="number" step="0.01" value={form.montant} onChange={e => set('montant', e.target.value)} className="mb-input" placeholder="Ex: 5000" />
            </div>
          )}

          <div className="mb-field">
            <label className="mb-label">Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} className="mb-input" rows={3} placeholder="Décrivez le Merci transmis..." />
          </div>

          <div className="mb-field">
            <label className="mb-label">Date</label>
            <input type="date" value={form.date_mpb} onChange={e => set('date_mpb', e.target.value)} className="mb-input" />
          </div>
        </div>
      </div>
    </div>
  );
}

function formatCA(n) {
  if (!n) return '0€';
  if (n >= 1000000) return `${(n/1000000).toFixed(1)}M€`;
  if (n >= 1000) return `${Math.round(n/1000)}k€`;
  return `${Math.round(n)}€`;
}
